/**
 * LIVEL paštomatų pasirinkimas — LP EXPRESS.
 *
 * LP EXPRESS OAuth (žr. worker/src/index.js getLpExpressAccessToken())
 * patvirtintas veikiantis 2026-09-08 su oficialiais TEST prisijungimais +
 * clientSystem=PUBLIC parametru. parseTerminal() žemiau naudoja PATVIRTINTUS
 * realaus /api/v2/terminal (TEST aplinka) atsakymo laukus.
 *
 * Struktūra veidrodinė cart-omniva.js atžvilgiu (paieška su matchScore()
 * rikiavimu, pasirinkimas, cart attributes išsaugojimas, checkout adreso
 * prefill, nemokamo pristatymo pranešimas, MutationObserver re-init po AJAX
 * cart atnaujinimo) — tie patys livel_delivery_* cart attribute raktai, tik
 * livel_delivery_carrier reikšmė "LP EXPRESS" vietoj "Omniva".
 *
 * Renderinama per snippets/cart-lpexpress-widget.liquid, wired į
 * snippets/cart-drawer.liquid (desktop + mobile drawer) ir
 * sections/main-cart.liquid (/cart puslapis) šalia cart-omniva-widget.
 * Viešai matoma tik kai settings.livel_lpexpress_enabled = true.
 */

const LPEXPRESS_TERMINALS_ENDPOINT = 'https://livel-parcel-lockers.dianakatharina64.workers.dev/lpexpress/terminals';
const LPEXPRESS_COUNTRY_CODE = 'LT';
const API_VERSION = '2025-10';
const PRICING_ENDPOINT = 'https://livel-parcel-lockers.dianakatharina64.workers.dev/pricing';

/** @type {Array<{name: string, address: string, city: string, zip: string, searchText: string}> | null} */
let terminalsCache = null;
/** @type {Promise<any> | null} */
let terminalsPromise = null;
/** @type {{ price: number | null, threshold: number | null } | null} */
let pricingCache = null;

function euros(cents) {
  return (cents / 100).toLocaleString('lt-LT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/**
 * Patvirtinta prieš realų LP EXPRESS TEST /api/v2/terminal atsakymą
 * (2026-09-08, žr. livel-parcel-lockers-status.md atmintį). Naudojami tik
 * patvirtinti laukai: id, name, address, city, postalCode, countryCode,
 * latitude, longitude. Vidinis "zip" laukas (naudojamas visur kitur šiame
 * faile, kad struktūra sutaptų su cart-omniva.js) šaltinis dabar yra
 * raw.postalCode (LP EXPRESS neturi lauko "zip").
 */
function parseTerminal(raw) {
  const name = raw.name || '';
  const address = raw.address || '';
  const city = raw.city || '';
  const zip = raw.postalCode || '';
  return {
    name,
    address,
    city,
    zip,
    searchText: [name, address, city, zip].filter(Boolean).join(' '),
    raw,
  };
}

function stripDiacritics(str) {
  return str.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function normalizeForSearch(str) {
  return stripDiacritics(str.toLowerCase());
}

const SEARCH_STEM_LENGTH = 4;

// Rikiavimo balai — perkelta iš cart-omniva.js matchScore() (žr. ten pilną
// paaiškinimą apie kodėl paprastas substring/stem filtras be rikiavimo
// leisdavo pvz. Kauno terminalams su gatve "Jonavos g." atsirasti PRIEŠ
// tikrus Jonavos miesto terminalus, ieškant "Jonava"). LP EXPRESS "city"
// laukas jau yra vardininko forma ("Vilnius", "Kaunas", "Jonava" — ne
// "Vilniaus m." kaip Omniva duomenyse), tad tikslaus žodžio atitikimas
// (w === q) čia dažnai suveiks tiesiogiai, be reikalo remtis vien šaknimi.
function matchScore(terminal, q, qStem) {
  const city = normalizeForSearch(terminal.city || '');
  const cityWords = city.split(/[\s,.-]+/).filter(Boolean);
  if (cityWords.some((w) => w === q || w.slice(0, qStem.length) === qStem)) return 3;

  const name = normalizeForSearch(terminal.name || '');
  if (name.startsWith(q) || name.split(/[\s,.-]+/).some((w) => w.length > 0 && w.slice(0, qStem.length) === qStem)) {
    return 2;
  }

  const haystack = normalizeForSearch(terminal.searchText);
  if (haystack.includes(q)) return 1;
  if (haystack.split(/[\s,.-]+/).some((word) => word.length > 0 && word.slice(0, qStem.length) === qStem)) return 0;
  return -1;
}

function searchTerminals(terminals, query) {
  const qRaw = query.trim();
  if (qRaw.length < 2) return [];
  const q = normalizeForSearch(qRaw);
  const qStem = q.slice(0, Math.min(SEARCH_STEM_LENGTH, q.length));

  const scored = [];
  for (const t of terminals) {
    const score = matchScore(t, q, qStem);
    if (score >= 0) scored.push({ terminal: t, score });
  }

  // Stabilus rikiavimas (ES2019+) — ta pati logika kaip cart-omniva.js.
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 20).map((s) => s.terminal);
}

async function fetchLpExpressTerminals() {
  if (terminalsCache) return terminalsCache;
  if (terminalsPromise) return terminalsPromise;

  terminalsPromise = (async () => {
    const res = await fetch(`${LPEXPRESS_TERMINALS_ENDPOINT}?receiverCountryCode=${LPEXPRESS_COUNTRY_CODE}`);
    if (!res.ok) throw new Error('LP EXPRESS terminals fetch failed: ' + res.status);
    const json = await res.json();
    if (!json.ok) throw new Error('LP EXPRESS terminals endpoint error: ' + (json.error || 'unknown'));
    const rawList = Array.isArray(json.terminals) ? json.terminals : json.terminals && json.terminals.data;
    terminalsCache = (Array.isArray(rawList) ? rawList : []).map(parseTerminal);
    return terminalsCache;
  })();

  return terminalsPromise;
}

async function getStorefrontCartId() {
  const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  const cart = await res.json();
  return { gid: `gid://shopify/Cart/${cart.token}`, cart };
}

async function storefrontQuery(query, variables) {
  const res = await fetch(`/api/${API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables }),
  });
  return res.json();
}

async function getPricing() {
  if (pricingCache) return pricingCache;
  try {
    const res = await fetch(PRICING_ENDPOINT, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Pricing endpoint failed: ' + res.status);
    const data = await res.json();
    const threshold = Number.isFinite(data.freeShippingThreshold)
      ? data.freeShippingThreshold
      : parseFloat(data.freeShippingThreshold);
    pricingCache = { threshold: Number.isFinite(threshold) ? Math.round(threshold * 100) : null };
  } catch (e) {
    pricingCache = { threshold: null };
  }
  return pricingCache;
}

// LIVEL: ta pati sinchronizacijos mechanika kaip cart-omniva.js (žr. ten
// pilną komentarą) — po sėkmingo pasirinkimo/išvalymo transliuojame ŠVIEŽIĄ
// cart.attributes būseną, kad Omniva widget'as iš karto atsinaujintų.
// 2026-09-08: detail dabar papildomai neša visą `cart` objektą — žr.
// cart-omniva.js tapatų komentarą.
function broadcastDeliverySelection(cart) {
  const attrs = (cart && cart.attributes) || {};
  document.dispatchEvent(new CustomEvent('livel:delivery-selection-changed', {
    detail: {
      carrier: attrs.livel_delivery_carrier || '',
      name: attrs.livel_delivery_locker_name || '',
      address: attrs.livel_delivery_locker_address || '',
      city: attrs.livel_delivery_locker_city || '',
      zip: attrs.livel_delivery_locker_zip || '',
      cart: cart || null
    }
  }));
}

// LIVEL: ta pati checkout shipping-method fix'o logika kaip cart-omniva.js
// (žr. ten pilną komentarą) — HANDLE reikšmės niekada nehardcodinamos,
// visada randamos dinamiškai iš cart.deliveryGroups.
async function setSelectedDeliveryOption(cartGid, titleKeywords) {
  try {
    const groupsResult = await storefrontQuery(
      `query LivelGetDeliveryGroups($cartId: ID!) {
        cart(id: $cartId) {
          deliveryGroups(first: 10) {
            edges { node { id deliveryOptions { handle title } } }
          }
        }
      }`,
      { cartId: cartGid }
    );
    const edges =
      (groupsResult.data &&
        groupsResult.data.cart &&
        groupsResult.data.cart.deliveryGroups &&
        groupsResult.data.cart.deliveryGroups.edges) ||
      [];

    let deliveryGroupId = null;
    let deliveryOptionHandle = null;
    for (const edge of edges) {
      const group = edge.node;
      const match = group.deliveryOptions.find((option) => {
        const title = (option.title || '').toLowerCase();
        return titleKeywords.some((keyword) => title.includes(keyword));
      });
      if (match) {
        deliveryGroupId = group.id;
        deliveryOptionHandle = match.handle;
        break;
      }
    }

    if (!deliveryGroupId || !deliveryOptionHandle) return;

    const updateResult = await storefrontQuery(
      `mutation LivelSetDeliveryOption($cartId: ID!, $selectedDeliveryOptions: [CartSelectedDeliveryOptionInput!]!) {
        cartSelectedDeliveryOptionsUpdate(cartId: $cartId, selectedDeliveryOptions: $selectedDeliveryOptions) {
          userErrors { field message code }
          warnings { message code }
        }
      }`,
      {
        cartId: cartGid,
        selectedDeliveryOptions: [{ deliveryGroupId, deliveryOptionHandle }]
      }
    );
    const payload = updateResult.data && updateResult.data.cartSelectedDeliveryOptionsUpdate;
    if (payload && ((payload.userErrors && payload.userErrors.length) || (payload.warnings && payload.warnings.length))) {
      // eslint-disable-next-line no-console
      console.warn('LIVEL cartSelectedDeliveryOptionsUpdate:', payload.userErrors, payload.warnings);
    }
  } catch (e) {
    // Convenience — cart attributes jau užtikrina merchant-visible
    // pasirinkimą; jei ši mutacija nepavyksta, klientas tiesiog turi pats
    // rankiniu būdu pasirinkti teisingą radio checkout'e (ankstesnė elgsena).
  }
}

async function saveSelection(root, terminal) {
  const address2 = `${root.dataset.carrierLabel} — ${terminal.name}`;

  setLoading(root, true);

  const updateRes = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: {
        livel_delivery_carrier: 'LP EXPRESS',
        livel_delivery_locker_name: terminal.name || '',
        livel_delivery_locker_address: terminal.address || '',
        livel_delivery_locker_city: terminal.city || '',
        livel_delivery_locker_zip: terminal.zip || '',
      },
    }),
  });
  const cart = await updateRes.json();

  const cartGid = `gid://shopify/Cart/${cart.token}`;
  try {
    await storefrontQuery(
      `mutation LivelSetAddress($cartId: ID!, $addresses: [CartSelectableAddressInput!]!) {
        cartDeliveryAddressesAdd(cartId: $cartId, addresses: $addresses) {
          userErrors { field message }
        }
      }`,
      {
        cartId: cartGid,
        addresses: [
          {
            address: {
              deliveryAddress: {
                address1: terminal.address || '',
                address2,
                city: terminal.city || '',
                zip: terminal.zip || '',
                countryCode: 'LT',
              },
            },
            selected: true,
            oneTimeUse: true,
          },
        ],
      }
    );
  } catch (e) {
    // Address prefill is a convenience — cart attributes above already
    // preserve the merchant-visible selection either way.
  }

  // Priverstinai pažymime teisingą checkout shipping method — žr.
  // setSelectedDeliveryOption() komentarą aukščiau.
  await setSelectedDeliveryOption(cartGid, ['lp express', 'lpexpress']);

  setLoading(root, false);
  applySelectedState(root, { name: terminal.name || '', address: terminal.address || '', city: terminal.city || '', zip: terminal.zip || '' });
  broadcastDeliverySelection(cart);
  await refreshFreeShippingMessage(root, cart);
}

async function clearSelection(root) {
  setLoading(root, true);
  const updateRes = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: {
        livel_delivery_carrier: '',
        livel_delivery_locker_name: '',
        livel_delivery_locker_address: '',
        livel_delivery_locker_city: '',
        livel_delivery_locker_zip: '',
      },
    }),
  });
  const cart = await updateRes.json();
  setLoading(root, false);
  applyIdleState(root);
  broadcastDeliverySelection(cart);
  await refreshFreeShippingMessage(root, cart);
}

function setLoading(root, isLoading) {
  const loadingEl = root.querySelector('[data-cart-lpexpress-loading]');
  if (loadingEl) loadingEl.hidden = !isLoading;
}

function applySelectedState(root, selection) {
  root.querySelector('[data-cart-lpexpress-idle]').hidden = true;
  const selectedEl = root.querySelector('[data-cart-lpexpress-selected]');
  selectedEl.hidden = false;
  root.querySelector('[data-cart-lpexpress-selected-name]').textContent = selection.name;
  root.querySelector('[data-cart-lpexpress-selected-address]').textContent =
    [selection.address, selection.city, selection.zip].filter(Boolean).join(', ');
}

function applyIdleState(root) {
  root.querySelector('[data-cart-lpexpress-selected]').hidden = true;
  root.querySelector('[data-cart-lpexpress-idle]').hidden = false;
  const searchInput = root.querySelector('[data-cart-lpexpress-search]');
  if (searchInput) searchInput.value = '';
  const resultsEl = root.querySelector('[data-cart-lpexpress-results]');
  if (resultsEl) {
    resultsEl.hidden = true;
    resultsEl.innerHTML = '';
  }
}

function renderResults(root, results) {
  const resultsEl = root.querySelector('[data-cart-lpexpress-results]');
  if (!resultsEl) return;

  if (results.length === 0) {
    resultsEl.hidden = false;
    resultsEl.innerHTML = `<li class="cart-lpexpress__result cart-lpexpress__result--empty">${root.dataset.noResultsText}</li>`;
    return;
  }

  resultsEl.hidden = false;
  resultsEl.innerHTML = '';
  for (const terminal of results) {
    const li = document.createElement('li');
    li.className = 'cart-lpexpress__result';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cart-lpexpress__result-button';
    button.addEventListener('click', () => saveSelection(root, terminal));

    const name = document.createElement('span');
    name.className = 'cart-lpexpress__result-name';
    name.textContent = terminal.name || '';

    const address = document.createElement('span');
    address.className = 'cart-lpexpress__result-address';
    address.textContent = [terminal.address, terminal.city].filter(Boolean).join(', ');

    button.append(name, address);
    li.append(button);
    resultsEl.appendChild(li);
  }
}

async function refreshFreeShippingMessage(root, cartMaybe) {
  const messageEl = root.querySelector('[data-cart-lpexpress-free-shipping]');
  if (!messageEl) return;

  const pricing = await getPricing();
  if (pricing.threshold == null) {
    messageEl.textContent = '';
    return;
  }

  let cart = cartMaybe;
  if (!cart) {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    cart = await res.json();
  }

  // total_price = po nuolaidos kodų — taip pat skaičiuoja Shopify free-shipping nuolaida (nuo 30 €).
  const subtotal = cart.total_price;

  if (subtotal >= pricing.threshold) {
    messageEl.textContent = root.dataset.freeShippingReachedText;
  } else {
    const remaining = pricing.threshold - subtotal;
    messageEl.textContent = root.dataset.freeShippingProgressText.replace('{amount}', euros(remaining));
  }
}

function wireSearch(root) {
  const input = root.querySelector('[data-cart-lpexpress-search]');
  const hint = root.querySelector('[data-cart-lpexpress-hint]');
  if (!input) return;

  let debounceTimer = null;

  input.addEventListener('input', () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(async () => {
      const query = input.value;
      if (query.trim().length < 2) {
        const resultsEl = root.querySelector('[data-cart-lpexpress-results]');
        if (resultsEl) {
          resultsEl.hidden = true;
          resultsEl.innerHTML = '';
        }
        if (hint) hint.hidden = false;
        return;
      }
      if (hint) hint.hidden = true;
      setLoading(root, true);
      try {
        const terminals = await fetchLpExpressTerminals();
        renderResults(root, searchTerminals(terminals, query));
      } catch (e) {
        renderResults(root, []);
      } finally {
        setLoading(root, false);
      }
    }, 250);
  });
}

function wireChangeButton(root) {
  const button = root.querySelector('[data-cart-lpexpress-change]');
  if (!button) return;
  button.addEventListener('click', () => clearSelection(root));
}

function initCartLpExpress(root) {
  if (root.dataset.cartLpexpressInitialized) return;
  root.dataset.cartLpexpressInitialized = 'true';

  wireSearch(root);
  wireChangeButton(root);

  if (root.dataset.selectedLockerName && root.dataset.selectedCarrier === 'LP EXPRESS') {
    applySelectedState(root, {
      name: root.dataset.selectedLockerName,
      address: root.dataset.selectedLockerAddress || '',
      city: root.dataset.selectedLockerCity || '',
      zip: root.dataset.selectedLockerZip || '',
    });
  } else {
    applyIdleState(root);
  }

  refreshFreeShippingMessage(root);
}

function initAll() {
  document.querySelectorAll('[data-cart-lpexpress]').forEach(initCartLpExpress);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

// Re-init after Shopify Section Rendering API swaps.
document.addEventListener('shopify:section:load', initAll);

// Re-init whenever a NEW, not-yet-wired [data-cart-lpexpress] element appears
// anywhere in the DOM — tas pats mechanizmas ir ta pati priežastis kaip
// cart-omniva.js (žr. ten pilną paaiškinimą: shopify:section:load niekada
// nefire'inasi šioje temoje pridedant prekę, o shopify:cart:lines-update +
// event.promise.then() suveikia PER ANKSTI, prieš DOM swap realiai
// įvyksta). MutationObserver nepriklauso nuo laiko — suveikia tiksliai
// tada, kai naujas elementas jau realiai yra DOM'e. Būtina tam, kad desktop
// cart drawer instancija veiktų teisingai po AJAX cart atnaujinimo (žr.
// snippets/cart-drawer.liquid komentarą apie script tag'o iškėlimą — ta
// pati priežastis taikoma ir šiam failui).
const cartLpExpressObserver = new MutationObserver(() => {
  const unwired = document.querySelector('[data-cart-lpexpress]:not([data-cart-lpexpress-initialized])');
  if (unwired) initAll();
});
cartLpExpressObserver.observe(document.body, { childList: true, subtree: true });

// Sinchronizacija su Omniva widget'u — žr. broadcastDeliverySelection()
// komentarą aukščiau ir tapatų klausytoją cart-omniva.js.
document.addEventListener('livel:delivery-selection-changed', (event) => {
  const detail = event.detail || {};
  document.querySelectorAll('[data-cart-lpexpress]').forEach((root) => {
    if (detail.carrier === 'LP EXPRESS' && detail.name) {
      applySelectedState(root, { name: detail.name, address: detail.address, city: detail.city, zip: detail.zip });
    } else {
      applyIdleState(root);
    }
  });
});
